import axios from 'axios';
import { ENDPOINTS, buildApiUrl } from '../config/api.config';

/**
 * Service for interacting with the CSV import API
 * Updated to work with both simplified imports and streaming imports
 */
class SimplifiedApiService {
  /**
   * Get the authentication token from local storage
   */
  private getAuthHeader() {
    // Call our local getAuthHeaders method to ensure consistency
    return this.getAuthHeaders();
  }

  /**
   * Get the auth headers using the centralized function
   */
  private getAuthHeaders() {
    // Only use 'authToken' to match the AuthContext.tsx implementation
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Determine if a file is large and should use streaming import
   * @param file The file to check
   * @returns boolean indicating if streaming should be used
   */
  private shouldUseStreaming(file: File): boolean {
    // Use streaming imports for files larger than 5MB
    const STREAMING_THRESHOLD = 5 * 1024 * 1024; // 5MB
    return file.size > STREAMING_THRESHOLD;
  }

  /**
   * Upload a sales data CSV file
   * Automatically chooses between standard and streaming endpoints based on file size
   */
  async uploadSalesFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Choose endpoint based on file size
      const useStreaming = this.shouldUseStreaming(file);
      const endpoint = useStreaming
        ? ENDPOINTS.STREAMING_IMPORTS.UPLOAD('sales_data')
        : ENDPOINTS.IMPORTS.SALES;

      const url = buildApiUrl(endpoint);
      console.log(`Uploading sales file to ${useStreaming ? 'streaming' : 'standard'} endpoint:`, url);

      const response = await axios.post(url, formData, {
        headers: {
          ...this.getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
        // Add timeout for large files
        timeout: useStreaming ? 180000 : 60000, // 3 minutes for streaming, 1 minute for standard
      });

      // Add metadata about which import type was used
      return {
        ...response.data,
        _import_method: useStreaming ? 'streaming' : 'standard'
      };
    } catch (error) {
      console.error('Error uploading sales file:', error);
      throw error;
    }
  }

  /**
   * Upload an ad spend CSV file
   * Automatically chooses between standard and streaming endpoints based on file size
   */
  async uploadAdspendFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Choose endpoint based on file size
      const useStreaming = this.shouldUseStreaming(file);
      const endpoint = useStreaming
        ? ENDPOINTS.STREAMING_IMPORTS.UPLOAD('ad_spend_data')
        : ENDPOINTS.IMPORTS.ADSPEND;

      const url = buildApiUrl(endpoint);
      console.log(`Uploading adspend file to ${useStreaming ? 'streaming' : 'standard'} endpoint:`, url);

      const response = await axios.post(url, formData, {
        headers: {
          ...this.getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
        // Add timeout for large files
        timeout: useStreaming ? 180000 : 60000, // 3 minutes for streaming, 1 minute for standard
      });

      // Add metadata about which import type was used
      return {
        ...response.data,
        _import_method: useStreaming ? 'streaming' : 'standard'
      };
    } catch (error) {
      console.error('Error uploading adspend file:', error);
      throw error;
    }
  }

  /**
   * Process an import with column mappings
   * Handles both standard and streaming imports
   */
  async processImport(importId: string, columnMapping: Record<string, string>, importMethod?: 'standard' | 'streaming') {
    try {
      // Determine if this is a streaming import based on the provided method or metadata in localStorage
      const isStreaming = importMethod === 'streaming' ||
        (localStorage.getItem(`import_${importId}_method`) === 'streaming');

      const endpoint = isStreaming
        ? ENDPOINTS.STREAMING_IMPORTS.PROCESS(importId)
        : ENDPOINTS.IMPORTS.PROCESS(importId);

      const url = buildApiUrl(endpoint);
      console.log(`Processing import at ${isStreaming ? 'streaming' : 'standard'} endpoint:`, url);

      const payload = {
        import_session_id: isStreaming ? undefined : importId, // Only needed for standard imports
        column_mapping: columnMapping,
      };

      const response = await axios.post(
        url,
        payload,
        {
          headers: {
            ...this.getAuthHeader(),
          },
          // Longer timeout for streaming imports
          timeout: isStreaming ? 300000 : 60000, // 5 minutes for streaming, 1 minute for standard
        },
      );

      // For streaming imports, we need to poll for status
      if (isStreaming) {
        // Return initial response, but indicate it needs status polling
        return {
          ...response.data,
          status: 'processing_started',
          needs_polling: true,
          _import_method: 'streaming'
        };
      }

      // Return standard response for regular imports
      return response.data;
    } catch (error) {
      console.error('Error processing import:', error);
      throw error;
    }
  }

  /**
   * Get sales data for an import session
   */
  async getSalesData(importId: string) {
    try {
      const url = buildApiUrl(ENDPOINTS.IMPORTS.GET_SALES(importId));
      console.log('Getting sales data from:', url);
      const response = await axios.get(url, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error('Error getting sales data:', error);
      throw error;
    }
  }

  /**
   * Get adspend data for an import session
   */
  async getAdspendData(importId: string) {
    try {
      const url = buildApiUrl(ENDPOINTS.IMPORTS.GET_ADSPEND(importId));
      console.log('Getting adspend data from:', url);
      const response = await axios.get(url, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error('Error getting adspend data:', error);
      throw error;
    }
  }

  /**
   * Validate sample data with column mappings
   * Handles both standard and streaming imports
   */
  async validateSampleData(
    importId: string,
    columnMapping: Record<string, string>,
    rowIndex?: number,
    sampleSize = 5,
    importMethod?: 'standard' | 'streaming'
  ) {
    try {
      // Determine if this is a streaming import
      const isStreaming = importMethod === 'streaming' ||
        (localStorage.getItem(`import_${importId}_method`) === 'streaming');

      const endpoint = isStreaming
        ? ENDPOINTS.STREAMING_IMPORTS.VALIDATION(importId)
        : ENDPOINTS.IMPORTS.VALIDATE(importId);

      const url = buildApiUrl(endpoint);
      console.log(`Validating sample data at ${isStreaming ? 'streaming' : 'standard'} endpoint:`, url);

      const response = await axios.post(
        url,
        {
          column_mapping: columnMapping,
          row_index: rowIndex,
          sample_size: sampleSize
        },
        {
          headers: this.getAuthHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error validating sample data:', error);
      throw error;
    }
  }
  /**
   * Get processing status for a streaming import
   * Only applicable for streaming imports
   */
  async getImportStatus(importId: string) {
    try {
      const url = buildApiUrl(ENDPOINTS.STREAMING_IMPORTS.STATUS(importId));
      console.log('Getting import status from:', url);
      const response = await axios.get(url, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error('Error getting import status:', error);
      throw error;
    }
  }

  /**
   * Cancel an in-progress streaming import
   * Only applicable for streaming imports
   */
  async cancelImport(importId: string) {
    try {
      const url = buildApiUrl(ENDPOINTS.STREAMING_IMPORTS.CANCEL(importId));
      console.log('Cancelling import at:', url);
      const response = await axios.post(
        url,
        {},
        {
          headers: this.getAuthHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling import:', error);
      throw error;
    }
  }

  /**
   * Get file metadata for an import
   */
  async getFileMetadata(importId: string) {
    try {
      // Check if this is a streaming import first
      const isStreaming = localStorage.getItem(`import_${importId}_method`) === 'streaming';

      let url;
      if (isStreaming) {
        url = buildApiUrl(`${ENDPOINTS.STREAMING_IMPORTS.STATUS(importId)}/file`);
      } else {
        // For standard imports, use the session endpoint
        url = buildApiUrl(`${ENDPOINTS.IMPORTS.PROCESS(importId)}/file`);
      }

      console.log('Getting file metadata from:', url);
      const response = await axios.get(url, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error('Error getting file metadata:', error);
      throw error;
    }
  }
}

export default new SimplifiedApiService();